import React, { useState, useEffect } from 'react';
import { auth, db } from '../config/firebase';
import { signOut, updatePassword } from 'firebase/auth'; // eslint-disable-line
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';

export default function ProfilePage() {
  const [profile, setProfile] = useState({
    name: '', brokerage: '', phone: '', email: '',
    notifyOnScan: true, notifyOnLead: true
  });
  const [newPassword, setNewPassword] = useState('');
  const [saved, setSaved] = useState(false);
  const [pwSaved, setPwSaved] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const user = auth.currentUser;

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      const docRef = doc(db, 'agents', user.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setProfile(prev => ({ ...prev, ...docSnap.data() }));
      } else {
        setProfile(prev => ({ ...prev, email: user.email || '' }));
      }
    };
    fetchProfile();
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    await setDoc(doc(db, 'agents', user.uid), profile, { merge: true });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    try {
      await updatePassword(user, newPassword);
      setNewPassword('');
      setPwSaved(true);
      setError('');
      setTimeout(() => setPwSaved(false), 3000);
    } catch (err) {
      setError(err.message.replace('Firebase: ', ''));
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>👤 Profile</h1>

      {/* Avatar */}
      <div style={styles.avatarSection}>
        <div style={styles.avatar}>
          {profile.name ? profile.name.charAt(0).toUpperCase() : '?'}
        </div>
        <p style={styles.avatarEmail}>{user?.email}</p>
      </div>

      {/* Profile Info */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Agent Info</h2>
        <input style={styles.input} placeholder="Full Name"
          value={profile.name} onChange={e => setProfile({...profile, name: e.target.value})} />
        <input style={styles.input} placeholder="Brokerage Name"
          value={profile.brokerage} onChange={e => setProfile({...profile, brokerage: e.target.value})} />
        <input style={styles.input} placeholder="Phone"
          value={profile.phone} onChange={e => setProfile({...profile, phone: e.target.value})} />
        <input style={styles.input} placeholder="Email"
          value={profile.email} onChange={e => setProfile({...profile, email: e.target.value})} />
        <button style={styles.button} onClick={handleSave}>
          {saved ? '✅ Saved!' : 'Save Changes'}
        </button>
      </div>

      {/* Notification Preferences */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Notifications</h2>
        <div style={styles.toggle}>
          <div>
            <p style={styles.toggleLabel}>Notify on QR Scan</p>
            <p style={styles.toggleSub}>Get alerted when someone scans your listing</p>
          </div>
          <input type="checkbox" checked={profile.notifyOnScan}
            onChange={e => setProfile({...profile, notifyOnScan: e.target.checked})}
            style={styles.checkbox} />
        </div>
        <div style={styles.toggle}>
          <div>
            <p style={styles.toggleLabel}>Notify on Lead</p>
            <p style={styles.toggleSub}>Get alerted when someone submits their info</p>
          </div>
          <input type="checkbox" checked={profile.notifyOnLead}
            onChange={e => setProfile({...profile, notifyOnLead: e.target.checked})}
            style={styles.checkbox} />
        </div>
        <button style={styles.button} onClick={handleSave}>
          {saved ? '✅ Saved!' : 'Save Preferences'}
        </button>
      </div>

      {/* Change Password */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Change Password</h2>
        {error && <p style={styles.error}>{error}</p>}
        <input style={styles.input} placeholder="New Password"
          type="password" value={newPassword}
          onChange={e => setNewPassword(e.target.value)} />
        <button style={styles.button} onClick={handlePasswordChange}>
          {pwSaved ? '✅ Password Updated!' : 'Update Password'}
        </button>
      </div>

      {/* Logout */}
      <div style={styles.section}>
        <button style={styles.logoutButton} onClick={handleLogout}>
          Log Out
        </button>
      </div>

      <NavBar />
    </div>
  );
}

const styles = {
  container: { maxWidth: 480, margin: '0 auto', padding: 20,
    fontFamily: 'sans-serif', paddingBottom: 100 },
  title: { fontSize: 24, fontWeight: 'bold', marginBottom: 20 },
  avatarSection: { display: 'flex', flexDirection: 'column',
    alignItems: 'center', marginBottom: 24 },
  avatar: { width: 80, height: 80, borderRadius: 40, background: '#2E86AB',
    color: '#fff', fontSize: 32, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontWeight: 'bold', marginBottom: 8 },
  avatarEmail: { color: '#666', fontSize: 14, margin: 0 },
  section: { marginBottom: 28 },
  sectionTitle: { fontSize: 18, fontWeight: 'bold', marginBottom: 12 },
  input: { width: '100%', padding: 12, marginBottom: 10, fontSize: 15,
    border: '1px solid #ddd', borderRadius: 8, boxSizing: 'border-box' },
  button: { width: '100%', padding: 14, background: '#2E86AB', color: '#fff',
    border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 'bold',
    cursor: 'pointer' },
  toggle: { display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 16, padding: 12,
    background: '#f9f9f9', borderRadius: 8 },
  toggleLabel: { fontSize: 15, fontWeight: 'bold', margin: '0 0 2px' },
  toggleSub: { fontSize: 12, color: '#999', margin: 0 },
  checkbox: { width: 20, height: 20, cursor: 'pointer' },
  error: { background: '#fee', color: '#c00', padding: 10,
    borderRadius: 8, fontSize: 14, marginBottom: 12 },
  logoutButton: { width: '100%', padding: 14, background: '#fff',
    color: '#e74c3c', border: '2px solid #e74c3c', borderRadius: 8,
    fontSize: 15, fontWeight: 'bold', cursor: 'pointer' }
};
